// ABOUTME: Pure resolver turning the visible nav candidates plus an `appBar` shape into the
// ABOUTME: ordered list of entries the AppBar renders (plain buttons and group dropdowns).

import type { AppBarShape, NavCandidate, NavEntry } from '../types';

// An item the tenant listed without an `order` sorts last within the configured block, keeping its
// built-in position as tie-breaker. Using 0 instead would teleport unordered items to the front.
const NO_ORDER = Number.MAX_SAFE_INTEGER;

// A sortable position for one rendered slot: `order` comes from the configuration, `defaultIndex`
// from the built-in bar and `id` makes the comparator a total order.
type Slot = { order: number; defaultIndex: number; id: string; entry: NavEntry };

const asItem = (item: NavCandidate): NavEntry => ({ kind: 'item', item });

const bySlotKey = (a: Slot, b: Slot) =>
    a.order - b.order || a.defaultIndex - b.defaultIndex || a.id.localeCompare(b.id);

/**
 * Applies a tenant-configured shape to the candidates.
 *
 * Candidates — never the config — drive the output, so ids with no matching candidate are silently
 * skipped and the setting can never make a nav item appear. With no shape (or an empty one) the
 * candidates are returned unchanged, which is the built-in bar.
 *
 * Configured items are emitted as one block **before** every item the tenant did not list: tenant
 * `order` values and built-in positions live in different number spaces and cannot be interleaved
 * deterministically.
 */
export function resolveAppBarLayout(
    candidates: NavCandidate[],
    shape: AppBarShape | null | undefined,
): NavEntry[] {
    if (!shape || (!shape.items?.length && !shape.groups?.length)) {
        return candidates.map(asItem);
    }

    // Last occurrence wins on duplicate ids.
    const cfgById = new Map((shape.items ?? []).map(item => [item.id, item]));
    const groupById = new Map((shape.groups ?? []).map(group => [group.id, group]));

    const configured = candidates.filter(c => {
        const cfg = cfgById.get(c.id);
        return cfg !== undefined && cfg.hidden !== true;
    });
    const unlisted = candidates.filter(c => !cfgById.has(c.id));

    const orderOf = (c: NavCandidate) => cfgById.get(c.id)?.order ?? NO_ORDER;

    // A `group` id with no matching group entry is ignored, leaving the item at top level.
    const groupIdOf = (c: NavCandidate) => {
        const groupId = cfgById.get(c.id)?.group;
        return groupId !== undefined && groupById.has(groupId) ? groupId : undefined;
    };

    const byItemKey = (a: NavCandidate, b: NavCandidate) =>
        orderOf(a) - orderOf(b) || a.defaultIndex - b.defaultIndex || a.id.localeCompare(b.id);

    const itemSlots: Slot[] = configured
        .filter(c => groupIdOf(c) === undefined)
        .map(c => ({
            order: orderOf(c),
            defaultIndex: c.defaultIndex,
            id: c.id,
            entry: asItem(c),
        }));

    const groupSlots: Slot[] = [...groupById.values()]
        .map(group => ({
            group,
            members: configured.filter(c => groupIdOf(c) === group.id).sort(byItemKey),
        }))
        .filter(bucket => bucket.members.length > 0)
        .map(({ group, members }) => ({
            // A group inherits the order of its earliest member unless it declares one of its own,
            // so a group whose members are all unordered falls to the end of the configured block.
            order: group.order ?? Math.min(...members.map(orderOf)),
            defaultIndex: Math.min(...members.map(m => m.defaultIndex)),
            id: group.id,
            // A single visible member does not deserve a dropdown.
            entry:
                members.length === 1
                    ? asItem(members[0])
                    : {
                          kind: 'group',
                          id: group.id,
                          label: group.label,
                          iconName: group.icon,
                          items: members,
                      },
        }));

    const configuredBlock = [...itemSlots, ...groupSlots].sort(bySlotKey).map(slot => slot.entry);
    const tail = [...unlisted].sort((a, b) => a.defaultIndex - b.defaultIndex).map(asItem);

    return [...configuredBlock, ...tail];
}
